import { useCallback, useEffect, useState } from 'react';
import { api, formatTarih, formatTl } from '../lib/api';
import { useAuth } from '../lib/auth';
import Ipucu from '../ui/Ipucu';
import FasonDetay from './FasonDetay';
import YeniFason from './YeniFason';

type Is = {
  id: number; no: string; title: string; status: string; statusLabel: string;
  contractor: { id: number; name: string; phone: string | null };
  costKurus: number | null; dosyaAdedi: number;
  createdAt: string; sonHareket: string; bekleyenGun: number;
  gecikti: boolean; linkAktif: boolean;
};
type Veri = { isler: Is[]; bekleGun: number };

const ROZET: Record<string, string> = {
  GONDERILDI: 'ccd-badge ccd-badge-info',
  KABUL: 'ccd-badge ccd-badge-info',
  BASLADI: 'ccd-badge ccd-badge-warning',
  BITTI: 'ccd-badge ccd-badge-success',
  SEVK: 'ccd-badge ccd-badge-success',
  IPTAL: 'ccd-badge',
};

const SUZGECLER = [
  { id: 'hepsi', ad: 'Tümü' },
  { id: 'GONDERILDI', ad: 'Gönderildi' },
  { id: 'KABUL', ad: 'Kabul etti' },
  { id: 'BASLADI', ad: 'Başladı' },
  { id: 'BITTI', ad: 'Bitirdi' },
  { id: 'SEVK', ad: 'Sevk edildi' },
];

export default function Fason() {
  const { can } = useAuth();
  const [durum, setDurum] = useState('hepsi');
  const [veri, setVeri] = useState<Veri | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [secili, setSecili] = useState<number | null>(null);
  const [yeniAcik, setYeniAcik] = useState(false);

  const yukle = useCallback(() => {
    api<Veri>(`/api/fason?durum=${durum}`).then(setVeri).catch((e) => setHata((e as Error).message));
  }, [durum]);
  useEffect(yukle, [yukle]);
  /* Fasonda bekleyen ürünler: verildi ama henüz teslim alınmadı. */
  const [fasonda, setFasonda] = useState<{ isId: number; no: string; fasoncu: string; durum: string; code: string; name: string; unit: string; kalan: number; gun: number }[]>([]);
  const fasondaYukle = useCallback(() => {
    api<{ satirlar: typeof fasonda }>('/api/fason/fasonda').then((r) => setFasonda(r.satirlar)).catch(() => undefined);
  }, []);
  useEffect(fasondaYukle, [fasondaYukle]);

  if (hata && !veri) {
    return (
      <div className="ccd-banner ccd-banner-danger">
        <span className="ccd-banner-icon" aria-hidden="true">!</span>
        <span>{hata}</span>
      </div>
    );
  }

  const geciken = veri?.isler.filter((i) => i.gecikti) ?? [];
  const acikIsler = veri?.isler.filter((i) => !['SEVK', 'IPTAL'].includes(i.status)) ?? [];

  return (
    <>
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="ccd-title">Fason İşler</h1>
          <div className="ccd-meta">
            {veri ? `${acikIsler.length} açık iş` : '…'} · fasoncular sisteme girmez,
            tek kullanımlık link ile ilerletir
          </div>
        </div>
        {can('fason.write') && (
          <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={() => setYeniAcik(true)} data-tur="yeni-fason">
            + Yeni İş Emri
          </button>
        )}
      </div>

      {geciken.length > 0 && (
        <div className="ccd-banner ccd-banner-warning">
          <span className="ccd-banner-icon" aria-hidden="true">⏳</span>
          <div>
            <div style={{ fontWeight: 600 }}>
              {geciken.length} iş {veri!.bekleGun} günden uzun süredir aynı durumda
            </div>
            <div className="ccd-meta">
              {geciken.slice(0, 3).map((i) => `${i.no} · ${i.contractor.name} (${i.bekleyenGun} gün)`).join(' · ')}
            </div>
          </div>
        </div>
      )}

      {fasonda.length > 0 && (
        <div className="ccd-card ccd-stack-sm">
          <div className="ccd-fieldset-title" style={{ margin: 0 }}>Fasonda bekleyen ürünler ({fasonda.length}) <Ipucu id="fasondaki-stok" /></div>
          {fasonda.map((f, n) => (
            <div key={n} className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
              <span><b className="ccd-mono">{f.code}</b> {f.name} <span className="ccd-meta">· {f.fasoncu} · {f.no} · {f.durum}{f.gun > 0 ? ` · ${f.gun} gündür` : ''}</span></span>
              <b>{f.kalan} {({ KUTU: 'kutu', M2: 'm²', ADET: 'adet', KG: 'kg', LT: 'lt', MTUL: 'metretül' } as Record<string, string>)[f.unit] ?? f.unit}</b>
            </div>
          ))}
          <div className="ccd-meta">İş bitince iş emrini açıp "Teslim al → stoğa gir" deyin; ürün stoğa girer ve buradan düşer.</div>
        </div>
      )}

      <div className="ccd-tabs" role="tablist">
        {SUZGECLER.map((s) => (
          <button key={s.id} role="tab" aria-selected={durum === s.id}
                  className="ccd-tab" onClick={() => setDurum(s.id)}>
            {s.ad}
          </button>
        ))}
      </div>

      {!veri ? (
        <div className="ccd-inline-note">Yükleniyor…</div>
      ) : veri.isler.length === 0 ? (
        <div className="ccd-empty">
          Bu süzgeçte iş yok.
          {can('fason.write') && ' "+ Yeni İş Emri" ile başlayın.'}
        </div>
      ) : (
        <div className="ccd-table-wrap">
          <table className="ccd-table">
            <thead>
              <tr>
                <th className="ccd-th">No</th>
                <th className="ccd-th">İş</th>
                <th className="ccd-th">Fasoncu</th>
                <th className="ccd-th">Durum <Ipucu id="fason-bekleyen" /></th>
                <th className="ccd-th ccd-td-num">Bekleyen <Ipucu id="fason-bekleyen" /></th>
                {can('quote.cost') && <th className="ccd-th ccd-td-num">Maliyet</th>}
                <th className="ccd-th">Son hareket</th>
              </tr>
            </thead>
            <tbody>
              {veri.isler.map((i) => (
                <tr key={i.id} className="ccd-tr ccd-tr-clickable" onClick={() => setSecili(i.id)}>
                  <td className="ccd-td ccd-mono ccd-td-nowrap">{i.no}</td>
                  <td className="ccd-td">
                    <div>{i.title}</div>
                    {i.dosyaAdedi > 0 && (
                      <div className="ccd-meta">📎 {i.dosyaAdedi} dosya</div>
                    )}
                  </td>
                  <td className="ccd-td">
                    {i.contractor.name}
                    {i.contractor.phone && <div className="ccd-meta">{i.contractor.phone}</div>}
                  </td>
                  <td className="ccd-td ccd-td-nowrap">
                    <span className={ROZET[i.status] ?? 'ccd-badge'}>{i.statusLabel}</span>
                  </td>
                  <td className="ccd-td ccd-td-num">
                    {['SEVK', 'IPTAL'].includes(i.status) ? (
                      <span className="ccd-muted">—</span>
                    ) : (
                      <span className={i.gecikti ? 'ccd-kpi-down' : ''}>{i.bekleyenGun} gün</span>
                    )}
                  </td>
                  {can('quote.cost') && (
                    <td className="ccd-td ccd-td-num">
                      {i.costKurus ? formatTl(i.costKurus) : <span className="ccd-muted">—</span>}
                    </td>
                  )}
                  <td className="ccd-td ccd-meta ccd-td-nowrap">{formatTarih(i.sonHareket)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {secili != null && (
        <FasonDetay
          id={secili}
          kapat={() => setSecili(null)}
          degisti={() => { yukle(); fasondaYukle(); }}
          yazabilir={can('fason.write')}
          maliyetGorur={can('quote.cost')}
        />
      )}
      {yeniAcik && (
        <YeniFason kapat={() => setYeniAcik(false)} eklendi={() => { setYeniAcik(false); yukle(); fasondaYukle(); }} />
      )}
    </>
  );
}
